import type { MemberCountryCode } from '../location/memberLocation';
import {
  SUPPORT_CANONICAL_SECTION,
  getCanonicalSupportCategory,
} from './supportCards';
import {
  getUsSupportRoute,
  type UsSupportRouteKey,
  type UsSupportRoutingMode,
} from './supportUsRouting';

type StaticSupportContentCard = {
  id: string;
  section: string;
  category: string;
  country_scope: MemberCountryCode;
  title: string;
  body: string;
  link_label?: string | null;
  link_url: string | null;
  routing_mode?: UsSupportRoutingMode | null;
  rules_vary_by_state?: boolean | null;
  sort_order: number;
  last_reviewed_at: string;
  is_active: boolean;
};

type UnitedStatesSupportCardDefinition = {
  title: string;
  body: string;
  routeKey: UsSupportRouteKey;
  routingMode: UsSupportRoutingMode;
  rulesVaryByState?: boolean;
};

const UNITED_STATES_SUPPORT_REVIEWED_AT = '2026-04-21T19:00:00.000Z';

const buildCategoryCards = (
  slug: string,
  definitions: readonly UnitedStatesSupportCardDefinition[],
): StaticSupportContentCard[] =>
  definitions.map((definition, index) => {
    const route = getUsSupportRoute(definition.routeKey);

    return {
      id: `static-us-${slug}-${index + 1}`,
      section: SUPPORT_CANONICAL_SECTION,
      category: getCanonicalSupportCategory(slug),
      country_scope: 'US',
      title: definition.title,
      body: definition.body,
      link_label: route.label,
      link_url: route.url,
      routing_mode: definition.routingMode,
      rules_vary_by_state: definition.rulesVaryByState ?? false,
      sort_order: index + 1,
      last_reviewed_at: UNITED_STATES_SUPPORT_REVIEWED_AT,
      is_active: true,
    };
  });

const UNITED_STATES_SUPPORT_CARD_DEFINITIONS: Record<
  string,
  readonly UnitedStatesSupportCardDefinition[]
> = {
  'money-benefits': [
    {
      title: 'Start with a benefits check',
      body:
        'Use a national benefits screener first. Best after separation, job loss, a move, a new baby, disability, or a big drop in income. In the U.S., many supports are federal but administered by states or local agencies.',
      routeKey: 'benefits_gov_finder',
      routingMode: 'federal',
    },
    {
      title: 'SNAP - food help through your state',
      body:
        'What food stamps cover, how interviews and recertification work, EBT basics, and what to do if your case changes. Each state has its own application, deductions, and local office process.',
      routeKey: 'snap_state_directory',
      routingMode: 'state_routed',
    },
    {
      title: 'WIC - support from pregnancy to age 5',
      body:
        'Healthy foods, formula and breastfeeding support, nutrition help, and local clinic appointments. State, territory, tribal, and local agencies run WIC differently, so always use the right local contact route.',
      routeKey: 'wic_contacts',
      routingMode: 'tribal_or_territory_routed',
    },
    {
      title: 'TANF / cash assistance - what your state may call welfare',
      body:
        'Cash help, work rules, time limits, diversion payments, sanctions, and what to do if your state uses a different program name. This is one of the biggest state-by-state variations in the U.S.',
      routeKey: 'tanf_start',
      routingMode: 'state_routed',
    },
    {
      title: 'Medicaid and CHIP - health coverage that depends on your state',
      body:
        'Where to apply, how renewals work, what happens if income changes, and why children may qualify even when an adult does not. State eligibility, managed care, and processing times vary.',
      routeKey: 'medicaid_state_overviews',
      routingMode: 'state_routed',
    },
    {
      title: 'Marketplace coverage - if Medicaid or CHIP is not the answer',
      body:
        'How ACA Marketplace plans fit in when you do not qualify for Medicaid or CHIP, and what to do if you are losing state coverage and need to avoid a gap.',
      routeKey: 'healthcare_gov_medicaid_chip',
      routingMode: 'federal',
    },
    {
      title: 'Child support - start, change, or chase a case',
      body:
        'How to open a case, establish parentage, ask for a review, enforce missed payments, and find the right office. State and tribal agencies run child support, not the federal office.',
      routeKey: 'child_support_contacts',
      routingMode: 'tribal_or_territory_routed',
    },
    {
      title: 'Child Tax Credit - do not assume you are not eligible',
      body:
        'Qualifying child basics, filing even when income is low, and why a tax return can still matter after separation or a change in who claims a child.',
      routeKey: 'irs_child_tax_credit',
      routingMode: 'federal',
    },
    {
      title: 'Earned Income Tax Credit - a big one to check if you work',
      body:
        'Who should re-check after a separation, move, custody change, or income drop. EITC rules depend on income, filing status, and who counts as your qualifying child.',
      routeKey: 'irs_eitc',
      routingMode: 'federal',
    },
    {
      title: 'Child and Dependent Care Credit',
      body:
        'Tax relief for child care or dependent care costs that let you work, look for work, or attend school. Good to check alongside state child care subsidy rules.',
      routeKey: 'irs_child_and_dependent_care_credit',
      routingMode: 'federal',
    },
    {
      title: 'Free tax filing and in-person tax help',
      body:
        'IRS Free File, VITA/TCE sites, what to bring, and why this matters when you are claiming family tax credits, changing filing status, or sorting out who claims a child.',
      routeKey: 'irs_free_tax_help',
      routingMode: 'local_directory',
    },
    {
      title: 'Child care financial assistance - subsidies vary by state',
      body:
        'How child care assistance works, what copays and waitlists can look like, and how to find the page your state uses for family applications.',
      routeKey: 'childcare_financial_assistance',
      routingMode: 'state_routed',
    },
    {
      title: 'Head Start and Early Head Start',
      body:
        'Free or low-cost early learning, eligibility, how local programs apply their own process, and how to find a center near you.',
      routeKey: 'head_start_locator',
      routingMode: 'local_directory',
    },
    {
      title: 'School meals - apply through the school or district',
      body:
        'Free or reduced-price meals, direct certification, and what it means when no application is needed because the school or district uses a community-wide option.',
      routeKey: 'school_meals_for_families',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Energy bill help - LIHEAP and weatherization',
      body:
        'Heating, cooling, utility crisis help, shutoff prevention, and weatherization. State and territory rules differ, so the right local office matters.',
      routeKey: 'liheap_start',
      routingMode: 'state_routed',
    },
    {
      title: 'SSI for a child with a disability',
      body:
        'When a child may qualify, what medical and school evidence helps, and how SSI can connect with Medicaid in many states.',
      routeKey: 'ssa_child_ssi',
      routingMode: 'federal',
    },
    {
      title: 'Survivor benefits after a parent dies',
      body:
        'When a child or surviving caregiver may be able to claim benefits, what documents you may need, and where to start if income has suddenly dropped after a death.',
      routeKey: 'ssa_survivors',
      routingMode: 'federal',
    },
    {
      title: 'Emergency food and local crisis help',
      body:
        'What to do if food has run out now: 211, local food programs, and the National Hunger Hotline. Use this when the month has broken before the paperwork catches up.',
      routeKey: 'emergency_food_help',
      routingMode: 'local_directory',
    },
  ],
  housing: [
    {
      title: 'Renting rights - start with your state, not a generic article',
      body:
        'Tenant rights in the U.S. are not one system. Start with your state attorney general, housing agency, or official tenant handbook, then get legal aid if the problem is already serious.',
      routeKey: 'tenant_rights',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Repairs, habitability, and code enforcement',
      body:
        'How to report serious repair issues, what habitability usually means, when to call city or county code enforcement, and why you should keep everything in writing.',
      routeKey: 'tenant_rights',
      routingMode: 'county_or_city_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Security deposits, fees, and screening rules',
      body:
        'Deposit returns, application fees, screening reports, and state rules on what a landlord can deduct or charge. These rules vary sharply by state and sometimes by city.',
      routeKey: 'tenant_rights',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Eviction notices and court papers - do not ignore them',
      body:
        'The difference between a warning, a formal notice, and a court case. Eviction rules, notice periods, and court timelines vary by state and local court.',
      routeKey: 'avoid_eviction',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Emergency rent help',
      body:
        'Where to look first if rent is due now: state or local emergency rental assistance, 211, nonprofits, and local housing agencies.',
      routeKey: 'emergency_rent_help',
      routingMode: 'county_or_city_routed',
    },
    {
      title: 'Public housing and subsidized housing',
      body:
        'The difference between public housing, project-based subsidized housing, and vouchers, plus how to find openings and waiting lists.',
      routeKey: 'rental_assistance',
      routingMode: 'local_directory',
    },
    {
      title: 'Housing Choice Voucher (Section 8)',
      body:
        'What a voucher is, who runs it, what happens on a waitlist, and why your local public housing agency is the real point of contact.',
      routeKey: 'section_8',
      routingMode: 'local_directory',
    },
    {
      title: 'Find your local public housing agency',
      body:
        'Use this when you need the right local office for vouchers, public housing, income updates, portability questions, or waitlist information.',
      routeKey: 'hud_pha_contacts',
      routingMode: 'local_directory',
    },
    {
      title: 'Housing discrimination - family status matters',
      body:
        'Refusals because you have children, disability-related barriers, harassment, and where to file a fair housing complaint.',
      routeKey: 'housing_discrimination',
      routingMode: 'federal',
    },
    {
      title: 'Domestic violence and housing safety',
      body:
        'Safety planning, emergency moves, confidentiality, and how housing rights can intersect with protection orders and local shelter access.',
      routeKey: 'domestic_violence_hotline',
      routingMode: 'local_directory',
    },
    {
      title: 'Emergency housing and shelter',
      body:
        'What to do when you may lose housing now: 211, local shelters, family shelters, youth routes, and emergency housing referrals.',
      routeKey: 'emergency_housing',
      routingMode: 'local_directory',
    },
    {
      title: 'Housing counseling before buying, moving, or when money is tight',
      body:
        'HUD-approved housing counselors can help with renting, buying, mortgage trouble, budgeting for housing, and foreclosure prevention.',
      routeKey: 'hud_housing_counselors',
      routingMode: 'local_directory',
    },
    {
      title: 'Utility shutoff help and keeping services on',
      body:
        'Energy crisis help, utility company payment plans, state moratorium rules where they exist, and local programs that can help keep the lights on.',
      routeKey: 'liheap_start',
      routingMode: 'local_directory',
    },
  ],
  legal: [
    {
      title: 'Custody and parenting time - the basics',
      body:
        'Legal custody, physical custody, parenting plans, and the language your state may use for parenting time or visitation. This is state law, not one national system.',
      routeKey: 'womenslaw_custody_basics',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Which state hears the custody case?',
      body:
        'The home state idea, moving before or after a case is filed, and why crossing a state line can change who has power to make orders.',
      routeKey: 'womenslaw_general_custody',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Child support orders - start, change, or enforce',
      body:
        'How support is calculated, how to ask for a review after income or care changes, and where arrears enforcement sits in your state or tribal system.',
      routeKey: 'child_support_contacts',
      routingMode: 'tribal_or_territory_routed',
    },
    {
      title: 'Establishing parentage / paternity',
      body:
        'Why this matters for child support, benefits, legal rights, and school or medical decisions, and how to find your state process.',
      routeKey: 'child_support_contacts',
      routingMode: 'tribal_or_territory_routed',
    },
    {
      title: 'Divorce and legal separation - where to start in your state',
      body:
        'Residency rules, waiting periods, filing fees, temporary orders, and how to find state-specific forms and self-help before you pay for a lawyer you may not need yet.',
      routeKey: 'lawhelp_org',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Protection orders / restraining orders',
      body:
        'Emergency orders, temporary orders, final orders, service, renewal, and what counts as a violation. The name and process differ by state.',
      routeKey: 'womenslaw_restraining_orders',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Domestic violence, custody, and safety planning',
      body:
        'How abuse can affect custody, exchanges, documentation, and court safety, plus why you may need specialist help rather than generic legal information.',
      routeKey: 'womenslaw_home',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Family court without a lawyer - forms and self-help',
      body:
        'Where to find state court self-help, guided forms, fee waiver information, and plain-language family law help if you are representing yourself.',
      routeKey: 'lawhelp_org',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Free legal aid - where to look first',
      body:
        'Use this for housing, family law, benefits, debt, and safety issues. Enter your city or ZIP to find funded legal aid near you.',
      routeKey: 'lsc_legal_help',
      routingMode: 'local_directory',
    },
    {
      title: 'Wills and guardianship - make a plan that fits your state',
      body:
        'Why single parents should name a guardian, make a will that works under state law, and review beneficiary designations and emergency paperwork.',
      routeKey: 'lsc_legal_help',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Passports, travel consent, and international worries',
      body:
        'Two-parent consent for a child passport, travel consent letters, and what to do if you are worried a child could be taken out of the country without your permission.',
      routeKey: 'travel_state_minors',
      routingMode: 'federal',
    },
  ],
  wellbeing: [
    {
      title: 'When the children are away - the quiet can hit hard',
      body:
        'Why the switch can feel strange even when you wanted the break, and how to make the time restorative instead of just empty.',
      routeKey: 'nami_support_groups',
      routingMode: 'local_directory',
    },
    {
      title: 'Loneliness as a single parent - it is real',
      body:
        'Why weekends, handover days, and nights can feel different, and where to look for support that is local enough to become real.',
      routeKey: 'help_211',
      routingMode: 'local_directory',
    },
    {
      title: 'Burnout - when everything sits on you',
      body:
        'What burnout can look like in a single-parent context, what to triage first, and where to get help if you are past coping and into survival.',
      routeKey: 'find_treatment',
      routingMode: 'local_directory',
    },
    {
      title: 'Guilt - work, money, screens, and survival',
      body:
        'A practical reset for the guilt that comes from doing too much and still feeling like it is not enough.',
      routeKey: 'nami_support_groups',
      routingMode: 'local_directory',
    },
    {
      title: 'Finding mental health care without getting lost',
      body:
        'Use local treatment and community health center locators when you need counseling, medication support, or a place to start that is affordable.',
      routeKey: 'find_treatment',
      routingMode: 'local_directory',
    },
    {
      title: 'Crisis support - call or text 988',
      body:
        'For mental health crisis, suicidal thoughts, overwhelming distress, or when you do not know who else to call. This is for you or someone you love.',
      routeKey: 'lifeline_988',
      routingMode: 'federal',
    },
    {
      title: 'Substance use help - where to start without shame',
      body:
        'Confidential treatment and recovery routes for alcohol or drug concerns, whether the problem is yours or someone close to you.',
      routeKey: 'samhsa_national_helpline',
      routingMode: 'local_directory',
    },
    {
      title: 'Domestic violence support - if home does not feel safe',
      body:
        'Confidential support, safety planning, local shelters, and next-step thinking when you are scared, controlled, or being hurt.',
      routeKey: 'domestic_violence_hotline',
      routingMode: 'local_directory',
    },
    {
      title: 'Building a local support network from scratch',
      body:
        'How to use 211, community health centers, school communities, Parent Centers, NAMI, and local services to build support in a place that still feels new.',
      routeKey: 'help_211',
      routingMode: 'local_directory',
    },
    {
      title: 'Sleep when stress keeps switching back on',
      body:
        'A practical reset when worry, grief, or hypervigilance are keeping you from real rest, plus where to get extra help if sleep loss is becoming a health issue.',
      routeKey: 'hrsa_health_centers',
      routingMode: 'local_directory',
    },
    {
      title: 'Movement and small routines that support your mood',
      body:
        'Not a perfection card. Just a reminder that gentle routines, short walks, sunlight, and structure can help when everything feels scattered.',
      routeKey: 'hrsa_health_centers',
      routingMode: 'local_directory',
    },
  ],
  'education-send': [
    {
      title: 'School enrollment - find the right district first',
      body:
        'How to identify your school district, find the right enrollment page, and avoid wasting time on the wrong office when you have just moved.',
      routeKey: 'nces_district_search',
      routingMode: 'school_district_routed',
    },
    {
      title: 'McKinney-Vento - if housing is unstable or you lose housing',
      body:
        'School stability rights, transportation help, immediate enrollment, and why you should ask for the district homeless liaison as early as possible.',
      routeKey: 'nche_mckinney_vento',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Free and reduced-price school meals',
      body:
        'How school meal applications work, who applies through the school or district, and how direct certification can mean you do not need to apply.',
      routeKey: 'school_meals_family_portal',
      routingMode: 'school_district_routed',
    },
    {
      title: 'When the whole school or district offers free meals',
      body:
        'Some high-poverty schools and districts serve breakfast and lunch to every student at no cost through CEP. This is worth checking before you assume you need an application.',
      routeKey: 'community_eligibility_provision',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Finding licensed child care and checking inspections',
      body:
        'How to find licensed care, child care resource and referral agencies, and the right state page for inspections and basic consumer information.',
      routeKey: 'childcare_state_resources',
      routingMode: 'state_routed',
    },
    {
      title: 'Reporting a child care concern',
      body:
        'Use your state or territory complaint route when you are worried about health and safety violations, supervision, or unsafe conditions.',
      routeKey: 'childcare_report_concern',
      routingMode: 'state_routed',
    },
    {
      title: 'Child care subsidies and waitlists',
      body:
        'How state child care assistance works, what counts as an approved provider, and why your state or territory page matters more than a general blog post.',
      routeKey: 'childcare_financial_assistance',
      routingMode: 'state_routed',
    },
    {
      title: 'Head Start and Early Head Start',
      body:
        'Early learning, family support, developmental screening, and local enrollment routes for children from birth to school entry.',
      routeKey: 'head_start_locator',
      routingMode: 'local_directory',
    },
    {
      title: 'Before-school, after-school, and summer options',
      body:
        'Use local school, district, parks, library, and child care resource routes to find coverage beyond the school day. This varies heavily by district and community.',
      routeKey: 'childcare_state_resources',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Special education - where to start if you are worried',
      body:
        'What Child Find means, how to raise concerns, and why written requests and records matter from the beginning.',
      routeKey: 'idea_parents_families',
      routingMode: 'school_district_routed',
    },
    {
      title: 'IEPs - what they are and how evaluation starts',
      body:
        'How to ask for an evaluation, consent basics, timelines, and what it means when a child needs specialized instruction, not just classroom tweaks.',
      routeKey: 'idea_initial_evaluations',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Section 504 plans - when accommodations may be enough',
      body:
        'How 504 works, what FAPE means under Section 504, and when a child may need accommodations even if they do not have an IEP.',
      routeKey: 'section_504',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Early intervention for babies and toddlers (birth to 3)',
      body:
        'Part C services, developmental delays, evaluations, family support, and why early help can make later school transitions easier.',
      routeKey: 'idea_part_c',
      routingMode: 'state_routed',
    },
    {
      title: 'Parent Centers - state help for school and disability rights',
      body:
        'Every state and territory has Parent Centers that help families understand evaluations, IEPs, 504 plans, disagreements, and local school systems.',
      routeKey: 'parent_center_hub',
      routingMode: 'state_routed',
    },
    {
      title: 'Disability discrimination at school - when to file an OCR complaint',
      body:
        'When disability access, exclusion, retaliation, or harassment may be a civil-rights issue and how to use the federal complaint route.',
      routeKey: 'ed_ocr_complaint',
      routingMode: 'federal',
    },
    {
      title: 'School discipline, suspension, and expulsion rules vary locally',
      body:
        'Discipline law is often state and district-specific. Ask for the district code of conduct, any disability-related protections, and local legal help early if exclusion is on the table.',
      routeKey: 'lawhelp_org',
      routingMode: 'school_district_routed',
      rulesVaryByState: true,
    },
    {
      title: 'ADHD - evaluation and school support',
      body:
        'There is no single test for ADHD. Start with your child doctor and school concerns in writing, and ask what support can begin while you wait for assessment.',
      routeKey: 'cdc_adhd',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Autism - screening, diagnosis, and early support',
      body:
        'What screening is, when concerns should be raised, and how medical routes and school routes often run side by side.',
      routeKey: 'cdc_autism',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Learning disabilities - ask for an evaluation in writing',
      body:
        'When reading, writing, math, processing, or attention concerns are affecting school, written requests and recordkeeping matter.',
      routeKey: 'idea_home',
      routingMode: 'school_district_routed',
    },
    {
      title: 'Child and teen mental health - when to seek help',
      body:
        'Warning signs, how assessment works, and how to start with a pediatrician, school team, or local mental health route without waiting for a total crisis.',
      routeKey: 'nimh_child_mental_health',
      routingMode: 'local_directory',
    },
  ],
  work: [
    {
      title: 'Family and Medical Leave Act (FMLA) - the federal floor',
      body:
        'Who the law covers, what job-protected unpaid leave can look like, and why you should also check your state for stronger protections or paid leave.',
      routeKey: 'dol_fmla',
      routingMode: 'federal',
    },
    {
      title: 'Paid family and medical leave - this varies by state',
      body:
        'Some states offer paid leave or stronger job protection for caregiving, bonding, or medical leave. Do not assume the federal rule is the whole answer.',
      routeKey: 'state_labor_offices',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Paid sick leave - federal law is not the full story',
      body:
        'There is no general federal paid sick leave requirement for most workers, so state and local rules can matter a lot.',
      routeKey: 'dol_sick_leave',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Pregnancy, pumping, and workplace accommodations',
      body:
        'Reasonable accommodations, pumping breaks and space, and what to do if you are pushed onto leave when adjustments could have kept you working.',
      routeKey: 'dol_maternal_health',
      routingMode: 'federal',
    },
    {
      title: 'Workplace discrimination and retaliation',
      body:
        'Where pregnancy, sex, disability, family-care responsibilities, or retaliation may cross into unlawful treatment, and how to start documenting concerns.',
      routeKey: 'eeoc_pregnancy_discrimination',
      routingMode: 'federal',
    },
    {
      title: 'Unemployment insurance - file in the state where you worked',
      body:
        'How to find the right state unemployment system, what to do if you worked in another state, and why appeal deadlines matter.',
      routeKey: 'careeronestop_unemployment',
      routingMode: 'state_routed',
    },
    {
      title: 'State labor office - your local starting point',
      body:
        'Use this when you need the right agency for wage complaints, state leave laws, final paycheck rules, or local labor standards.',
      routeKey: 'state_labor_offices',
      routingMode: 'state_routed',
    },
    {
      title: 'Minimum wage, overtime, and state labor standards',
      body:
        'Federal law sets a floor, but your state may do more. Check the state rule before assuming the federal answer is the only answer.',
      routeKey: 'state_labor_offices',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
    {
      title: 'Returning to work after a gap',
      body:
        'How to rebuild a work plan that accounts for child care, transport, school schedules, benefits, and what kind of role is realistic right now.',
      routeKey: 'careeronestop_local_help',
      routingMode: 'local_directory',
    },
    {
      title: 'Free job centers and local training',
      body:
        'American Job Centers, workforce services, training, resume help, and local employment support that can be more useful than generic online advice.',
      routeKey: 'careeronestop_local_help',
      routingMode: 'local_directory',
    },
    {
      title: 'Self-employment - the basics',
      body:
        'Sole proprietor basics, business records, what an EIN is for, and when to think about separate accounts and simple systems from day one.',
      routeKey: 'irs_self_employed_tax_center',
      routingMode: 'federal',
    },
    {
      title: 'Estimated taxes and quarterly payments',
      body:
        'When you may need to pay during the year, what happens if income is uneven, and how to avoid a nasty surprise later.',
      routeKey: 'irs_estimated_taxes',
      routingMode: 'federal',
    },
    {
      title: 'Self-employment tax and recordkeeping',
      body:
        'Self-employment tax, records you need to keep, and why good bookkeeping matters more when you are parenting alone and do not have spare time for chaos.',
      routeKey: 'irs_sole_proprietors',
      routingMode: 'federal',
    },
    {
      title: 'Health coverage when you are self-employed or lose job coverage',
      body:
        'Marketplace coverage, Medicaid/CHIP checks for children, and how to avoid a gap if your job-based insurance ends.',
      routeKey: 'healthcare_gov_medicaid_chip',
      routingMode: 'state_routed',
    },
    {
      title: 'When you are sick or injured and cannot work',
      body:
        'Federal unpaid leave, state leave or disability routes where they exist, workers compensation if the injury is job-related, and where to start locally.',
      routeKey: 'state_labor_offices',
      routingMode: 'state_routed',
      rulesVaryByState: true,
    },
  ],
};

export const getUnitedStatesStaticSupportCategoryCards = (
  slug: string,
  countryCode?: string | null,
): StaticSupportContentCard[] => {
  if (countryCode !== 'US') {
    return [];
  }

  const definitions = UNITED_STATES_SUPPORT_CARD_DEFINITIONS[slug];
  return definitions ? buildCategoryCards(slug, definitions) : [];
};
